import React, { useEffect, useState } from 'react'
import { UTCDateToSystemString, currentDateTimeInUTC, dateToUTCString } from '../../../../../_helpers/dates'
import {
  commitSchedulePurchase,
  getBusinessTerms,
  proposeSchedulePurchase,
  unapproveInvoiceSchedule,
} from '../../../../../_services/lender.service'

import { BusinessFactoringFees } from '../../../../LenderViews/BusinessFactoringFees'
import { Button } from '../../../../../_components/Button'
import { ErrorGroup } from '../../../../../_components/ErrorGroup'
import { LENDER_ROUTES } from '../../../../../_constants/routes'
import { LoaderButton } from '../../../../../_components/LoaderButton'
import { NotifyDebtors } from '../../../../../_components/modals/notify_debtors/NotifyDebtors'
import { PurchaseBlockerModal } from '../../../../../_components/modals/scheduleInvoices/PurchaseBlockerModal'
import { UPLOAD_ERRORS } from '../../../../ClientViews/UploadInvoices/constants'
import { WarningModal } from '../../../../../_components/modals/Warning/WarningModal'
import { logError } from '../../../../../_helpers/errors'
import { notificationActionHooks } from '../../../../../_actionHooks'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import { useModal } from '../../../../../_reduxHooks/modal.hook'
import { useScheduleInvoices } from '../../../../../_reduxHooks/scheduleInvoices.hook'

const S_TermsPrompt = styled.div`
  &.text-prompt {
    max-width: 450px;
  }
  h3 {
    margin-bottom: 32px;
  }
  .description {
    margin-bottom: 32px;
  }
  .buttons {
    display: flex;
    justify-content: space-between;
  }
  .table-controls-btn {
    margin-top: 32px;
    margin-left: 8px;
  }
`

const dateableStates = ['approved', 'closed', 'rejected', 'purchased']
export const SchedulePurchaseControls = (props) => {
  let schedule = props.schedule
  let reload = props.reload
  let updateSchedule = props.updateSchedule
  let business = props.business
  let locked = props.locked

  const renderNotifyDebtorsButton = props.renderNotifyDebtorsButton || (() => null)
  const renderUnprocessButton = props.renderUnprocessButton || (() => null)
  const defaultPurchaseDateTime = UTCDateToSystemString(currentDateTimeInUTC())
  const defaultPurchaseDate = defaultPurchaseDateTime.split('T')[0]

  const showNotification = notificationActionHooks.useShowNotification()

  const [purchasing, setPurchasing] = useState(false)
  const [unapproving, setUnapproving] = useState(false)
  const [purchaseDate, setPurchaseDate] = useState(defaultPurchaseDate)

  const purchaseDateChange = (e) => {
    let dateParts = e.target.value.split('-')
    let year = parseInt(dateParts[0])
    let month = parseInt(dateParts[1]) - 1
    let day = parseInt(dateParts[2])
    let hours = 0
    const date = new Date(Date.UTC(year, month, day, hours))
    const dateTime = dateToUTCString(date)
    setPurchaseDate(dateTime)
  }

  useEffect(() => {
    if (props.purchasePassThrough) {
      props.purchasePassThrough(purchasing)
    }
  }, [purchasing])

  useEffect(() => {
    if (!props.isModalOpen) {
      setPurchasing(false)
    }
  }, [props.isModalOpen])

  const defaultScheduleValue = schedule.purchase_date ? schedule.purchase_date.split('T')[0] : defaultPurchaseDate
  const { openModal } = useModal()

  const { setPurchaseShouldNotify } = useScheduleInvoices()

  const showBlockerModal = () => {
    openModal(PurchaseBlockerModal, {}, { modalTitle: 'Your Invoices Are Being Purchased', titleAlign: 'center' })
  }

  const purchaseWithTimeout = async (scheduleParam, proposal) => {
    const timeoutFlag = 'timeout'
    const duration = 10000

    let waitForPurchase = new Promise((resolve) => {
      let timeout = setTimeout(() => {
        clearTimeout(timeout)
        resolve(timeoutFlag)
      }, duration)
    })

    let race = Promise.race([
      waitForPurchase,
      commitSchedulePurchase(
        scheduleParam.id,
        proposal.date,
        proposal.batch_number,
        proposal.accounts,
        proposal.notify
      ),
    ])

    race
      .then((e) => {
        if (e === timeoutFlag) {
          setPurchaseShouldNotify(true)
          showBlockerModal()
        } else {
          setPurchaseShouldNotify(false)
          reload()
        }
        setPurchasing(false)
      })
      .catch((e) => {
        setPurchasing(false)
        showNotification({
          type: `error`,
          message: `${UPLOAD_ERRORS.SCHEDULE_UPLOAD__UNEXPECTED_ERROR}`,
        })
        console.error(e)
      })
      .finally(() => {
        setPurchasing(false)
      })
  }

  const proposeAndPurchase = async ({
    business,
    closeModal,
    openModal,
    purchaseDate,
    schedule,
    setPurchasing,
    showNotification,
  }) => {
    try {
      setPurchasing(true)
      let terms = await getBusinessTerms(schedule.business_id)
      let advanceRate = terms.find((t) => t.type == 'AdvanceRate' && t.submitted && t.value)
      if (!advanceRate) {
        openModal(() => {
          const [setFactoringFees] = useState(false)
          const history = useHistory()

          const goToSettings = () => {
            setPurchasing(false)
            history.push(`${LENDER_ROUTES.clients_overview}/${schedule.business_id}/settings`)
            closeModal()
          }
          if (setFactoringFees) {
            return (
              <S_TermsPrompt>
                <BusinessFactoringFees businessId={schedule.business_id} isModal={closeModal} />
              </S_TermsPrompt>
            )
          } else {
            return (
              <S_TermsPrompt className={'text-prompt'}>
                <h3>Set Terms</h3>
                <div className={'description'}>
                  It looks like terms have not yet been set up for this client. You must set at least an advance rate
                  before purchasing a client&apos;s schedule. Would you like to set up terms now?
                </div>
                <div className={'buttons'}>
                  <Button
                    onClick={() => {
                      setPurchasing(false)
                      closeModal()
                    }}
                  >
                    No
                  </Button>
                  <Button onClick={goToSettings}>Yes</Button>
                </div>
              </S_TermsPrompt>
            )
          }
        })
      } else {
        let proposal = null
        let formattedPurchaseDate
        try {
          formattedPurchaseDate = dateToUTCString(purchaseDate)
          proposal = await proposeSchedulePurchase(schedule.id, formattedPurchaseDate)
        } catch (e) {
          openModal(ErrorGroup, { title: 'Error', description: '', errors: [e] })
          throw e
        }
        const finish = async () => {
          const handleCommit = async () => {
            try {
              await purchaseWithTimeout(schedule, proposal)
            } catch (err) {
              console.error(err)
              setPurchasing(false)
              showNotification({
                type: `error`,
                message: `${UPLOAD_ERRORS.SCHEDULE_UPLOAD__UNEXPECTED_ERROR}`,
              })
            }
          }

          if (proposal.notifiable_debtors) {
            openModal(NotifyDebtors, {
              debtors: proposal.notifiable_debtors,
              invoiceScheduleId: schedule.id,
              onSelectionFinished: async (debtor_ids) => {
                proposal.notify = debtor_ids
                await handleCommit()
                closeModal()
              },
            })
          } else {
            closeModal()
            await handleCommit()
          }
        }
        if (proposal.alerts && Object.keys(proposal.alerts).length > 0) {
          let titleMap = {
            business_limit_exceeded: 'Business Credit Limit Exceeded',
          }
          openModal(() => {
            const fatalErrors = []
            if (business.client_debtor_relation_required) {
              fatalErrors.push(
                'overall_limit_not_set',
                'overall_concentration_not_set',
                'limit_not_set',
                'concentration_not_set'
              )
            }
            const fatalAlerts = fatalErrors.reduce((obj, key) => {
              if (Object.keys(proposal.alerts).includes(key)) {
                obj[key] = proposal.alerts[key]
              }
              return obj
            }, {})
            return (
              <WarningModal
                body={() => {
                  if (Object.keys(fatalAlerts).length === 0) {
                    return (
                      <ErrorGroup
                        className="warning"
                        description=""
                        errors={Object.keys(proposal.alerts).map((a) => proposal.alerts[a])}
                        title={
                          Object.keys(proposal.alerts).length > 1
                            ? 'Warning'
                            : titleMap[Object.keys(proposal.alerts)[0]]
                        }
                      />
                    )
                  } else {
                    return (
                      <ErrorGroup
                        className="error"
                        description=""
                        errors={Object.keys(fatalAlerts).map((a) => fatalAlerts[a])}
                        title={Object.keys(fatalAlerts).length > 1 ? 'Error' : titleMap[Object.keys(fatalAlerts)[0]]}
                      />
                    )
                  }
                }}
                declineLabel={Object.keys(fatalAlerts).length === 0 ? 'Cancel' : 'Okay'}
                onAccept={() => {
                  finish().then(() => {
                    setPurchasing(false)
                  })
                }}
                onDecline={() => {
                  setPurchasing(false)
                }}
                preventCloseModalOnAccept={true}
                showAccept={Object.keys(fatalAlerts).length === 0}
              />
            )
          })
        } else {
          finish()
        }
      }
    } catch (e) {
      logError(e)
      setPurchasing(false)
    }
  }

  return dateableStates.includes(schedule.state) ? (
    <div className={`purchase-details`}>
      <div className={`purchase-date`}>
        <div className={`label`}>Purchase Date</div>
        <input
          className={`schedule-date`}
          defaultValue={defaultScheduleValue}
          disabled={schedule.state != 'approved' || locked}
          max={defaultPurchaseDate}
          min={schedule.max_invoice_date}
          onChange={purchaseDateChange}
          placeholder="Purchase Date"
          type="date"
        />
      </div>
      <div className={`controls`}>
        <div className={`btn-container`}>
          {schedule.state == 'approved' && (
            <>
              <LoaderButton
                className={'secondary purchase-btn'}
                disabled={purchasing}
                isLoading={unapproving || locked}
                onClick={async () => {
                  setUnapproving(true)
                  try {
                    await unapproveInvoiceSchedule(schedule.id).then((schedule) => updateSchedule(schedule))
                  } catch (err) {
                    showNotification({
                      type: `error`,
                      message: `The invoice schedule can not be un-approved at the moment, please refresh the page.`,
                    })
                  }
                  setUnapproving(false)
                }}
              >
                Unapprove
              </LoaderButton>
              <LoaderButton
                className={`purchase-btn`}
                disabled={unapproving || schedule.state !== 'approved'}
                isLoading={purchasing || locked}
                onClick={() => {
                  proposeAndPurchase({
                    business,
                    closeModal: props.closeModal,
                    openModal: props.openModal,
                    purchaseDate,
                    schedule,
                    setPurchasing,
                    showNotification,
                  })
                }}
              >
                Purchase
              </LoaderButton>
            </>
          )}
          {renderNotifyDebtorsButton()}
          {renderUnprocessButton()}
        </div>
      </div>
    </div>
  ) : null
}
